import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { getSingletonHighlighter } from 'shiki';
import { cn } from '@/ui/lib/utils';
import { Button } from './button';
import { ScrollArea, ScrollBar } from './scroll-area';

type SupportedLang = 'ts' | 'js' | 'python' | 'html' | 'bash' | 'java';

const LANG_MAP: Record<SupportedLang, string> = {
	ts: 'typescript',
	js: 'javascript',
	python: 'python',
	html: 'html',
	bash: 'bash',
	java: 'java',
};

interface CodeBlockProps {
	code: string;
	language?: SupportedLang;
	className?: string;
}

export function CodeBlock({ code, language, className }: CodeBlockProps) {
	const [html, setHtml] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		const shikiLang = language ? (LANG_MAP[language] ?? 'text') : 'text';

		getSingletonHighlighter({
			themes: ['github-dark-default'],
			langs: [shikiLang],
		}).then((highlighter) => {
			const result = highlighter.codeToHtml(code, {
				lang: shikiLang,
				theme: 'github-dark-default',
			});
			setHtml(result);
		});
	}, [code, language]);

	const handleCopy = () => {
		navigator.clipboard.writeText(code);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<div
			className={cn(
				'relative group rounded-md overflow-hidden',
				className,
			)}
		>
			<Button
				type='button'
				variant='ghost'
				size='icon'
				onClick={handleCopy}
				className='absolute top-1.5 right-1.5 z-10 h-7 w-7 text-white/50 hover:text-white hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity'
			>
				{copied ? (
					<Check className='size-3.5 text-green-400' />
				) : (
					<Copy className='size-3.5' />
				)}
			</Button>

			{html ? (
				<ScrollArea>
					<div
						className='text-xs [&>pre]:bg-transparent! [&>pre]:px-4 [&>pre]:py-3 [&>pre]:m-0 [&>pre]:font-mono'
						style={{ background: '#0d1117' }}
						// biome-ignore lint/security/noDangerouslySetInnerHtml: shiki output is safe
						dangerouslySetInnerHTML={{ __html: html }}
					/>
					<ScrollBar orientation='horizontal' />
				</ScrollArea>
			) : (
				<pre
					className='px-4 py-3 text-xs font-mono text-white/70 whitespace-pre overflow-x-auto'
					style={{ background: '#0d1117' }}
				>
					{code}
				</pre>
			)}
		</div>
	);
}
